import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import {
  AuthUser,
  FixMeLater,
  LoginResponse,
  UserProperties,
  WorkspaceType,
} from "@shared/types";
import { get } from "lodash";
import { AUTH_SLICE_PATH } from "@shared/common/constants";
import { ApiPerFeature } from "@shared/services/endpoints/types";
import { IStorageService } from "@shared/utils/IStorageService";
import SocketHandler from "@shared/services/socket/socketHandler";
import { selectWorkspace } from "@shared/slices/common.slice";

export interface AuthState {
  token: string;
  refreshToken: string;
  isLoaded: boolean;
  user: UserProperties | undefined;
  demoUser: UserProperties | undefined;
}

export const createAuthSlice = (
  storageService: IStorageService,
  authApi: ApiPerFeature[typeof AUTH_SLICE_PATH],
  socketHandler: SocketHandler
) => {
  const InitialState = () => {
    let state: AuthState = {
      token: "",
      refreshToken: "",
      isLoaded: false,
      user: undefined,
      demoUser: undefined,
    };

    return state;
  };

  const _setTokens = (
    state: AuthState,
    token: string | undefined,
    refreshToken: string | undefined
  ) => {
    if (token) {
      state.token = token;
    }
    if (refreshToken) {
      state.refreshToken = refreshToken;
    }

    return storageService.setToken(state.token, state.refreshToken);
  };

  const _clearTokens = (state: FixMeLater) => {
    state.token = "";
    state.refreshToken = "";
    return storageService.clearToken();
  };
  const reducers = {
    clear(state: AuthState) {
      _clearTokens(state);
      state.user = undefined;
    },
    setTokens(
      state: AuthState,
      action: PayloadAction<{ token?: string; refresh_token?: string }>
    ) {
      const { token, refresh_token } = action.payload;
      _setTokens(state, token, refresh_token);
    },
    setLoaded(state: AuthState) {
      state.isLoaded = true;
    },
    setUser(
      state: AuthState,
      action: PayloadAction<{ user: UserProperties; isDemo?: boolean }>
    ) {
      const { user, isDemo } = action.payload;
      state[isDemo ? "demoUser" : "user"] = user;
    },
    updateUser(
      state: AuthState,
      action: PayloadAction<{ key: keyof UserProperties; value: any }>
    ) {
      const { key, value } = action.payload;
      if (state.user) {
        state.user = { ...state.user, [key]: value };
      }
      if (state.demoUser) {
        state.demoUser = { ...state.demoUser, [key]: value };
      }
    },
  };

  const authSlice = createSlice({
    name: AUTH_SLICE_PATH,
    initialState: InitialState,
    reducers,
    extraReducers: (builder) => {
      builder.addMatcher(
        isAnyOf(
          authApi.endpoints.login.matchFulfilled,
          authApi.endpoints.register.matchFulfilled,
          authApi.endpoints.connectToTelegram.matchFulfilled,
          authApi.endpoints.oauthUser.matchFulfilled,
          authApi.endpoints.verifyConfirmEmailCodeByUUID.matchFulfilled,
          authApi.endpoints.validateWebAppInitData.matchFulfilled
        ),
        (state: AuthState, action: PayloadAction<LoginResponse | void>) => {
          const { token, refresh_token } = action.payload || {};
          _setTokens(state, token, refresh_token);
        }
      );
      builder.addMatcher(
        isAnyOf(
          authApi.endpoints.logout.matchFulfilled,
          authApi.endpoints.logout.matchRejected
        ),
        (state: AuthState) => {
          _clearTokens(state);
          state.user = undefined;
          socketHandler.clear();
        }
      );
    },
  });

  const getStoreFromSliceName = (state: FixMeLater, sliceName: string) =>
    get(state, sliceName.split("/"), {});
  // Selectors
  const selectAuthStore = (state: FixMeLater): AuthState =>
    getStoreFromSliceName(state, AUTH_SLICE_PATH);
  const selectToken = createSelector(
    selectAuthStore,
    (authStore) => authStore.token
  );
  const selectRefreshToken = createSelector(
    selectAuthStore,
    (authStore) => authStore.refreshToken
  );
  const selectUser = createSelector(
    selectAuthStore,
    selectWorkspace,
    (authStore, workspace): AuthUser | undefined => {
      const user =
        workspace === WorkspaceType.DEMO ? authStore.demoUser : authStore.user;
      return user && new AuthUser(user);
    }
  );
  const selectRealUser = createSelector(
    selectAuthStore,
    (authStore): AuthUser | undefined => {
      return authStore.user && new AuthUser(authStore.user);
    }
  );
  const selectIsLoaded = createSelector(
    selectAuthStore,
    (authStore) => authStore.isLoaded
  );

  const authSelectors = {
    selectToken,
    selectRefreshToken,
    selectUser,
    selectRealUser,
    selectIsLoaded,
  };

  return { authSlice, authSelectors };
};
